import { useState } from 'react';
import { Card, Collapse, Tag, Descriptions, Empty, Badge, Divider } from 'antd';
import {
    ClockCircleOutlined,
    MedicineBoxOutlined,
    FileTextOutlined,
    ExperimentOutlined,
    HeartOutlined,
    UserOutlined,
    CalendarOutlined
} from '@ant-design/icons';
import moment from 'moment';
import './ConsultationHistory.css';

const { Panel } = Collapse;

const ConsultationHistory = ({ consultationData }) => {
    const [expandedSections, setExpandedSections] = useState(['basic']);

    if (!consultationData || consultationData.length === 0) {
        return (
            <Card className="consultation-history-card" style={{ marginBottom: '24px' }}>
                <Empty
                    description="No previous consultation data available"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            </Card>
        );
    }

    // Get the most recent consultation (first item in array)
    const consultation = consultationData[0];

    const renderMedicines = () => {
        if (!consultation.recomendedMedicines || consultation.recomendedMedicines.length === 0) {
            return <Empty description="No medicines prescribed" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
        }

        return (
            <div className="medicines-grid">
                {consultation.recomendedMedicines.map((medicine, index) => (
                    <Card
                        key={medicine._id || index}
                        size="small"
                        className="medicine-card"
                        bordered={false}
                    >
                        <div className="medicine-header">
                            <MedicineBoxOutlined className="medicine-icon" />
                            <h4>{medicine.medicineName}</h4>
                        </div>
                        <Descriptions column={1} size="small" className="medicine-details">
                            <Descriptions.Item label="Duration">{medicine.days} days</Descriptions.Item>
                            <Descriptions.Item label="Dosage">{medicine.howMuch}</Descriptions.Item>
                            <Descriptions.Item label="When to Take">
                                {medicine.whenToTake?.join(', ') || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Anupan">{medicine.anupan || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Course">{medicine.course || 'N/A'}</Descriptions.Item>
                        </Descriptions>
                    </Card>
                ))}
            </div>
        );
    };

    const renderDiagnosis = () => {
        if (!consultation.diagnosis || consultation.diagnosis.length === 0) return null;

        return (
            <div className="diagnosis-section">
                {consultation.diagnosis.map((item, index) => (
                    <div key={index} className="diagnosis-item">
                        <strong>{item.diagnosis}</strong>
                        {item.other && <p className="diagnosis-note">{item.other}</p>}
                    </div>
                ))}
            </div>
        );
    };

    const renderTreatments = () => {
        if (!consultation.treatment || consultation.treatment.length === 0) return null;

        return (
            <div className="treatment-list">
                {consultation.treatment.map((item, index) => (
                    <Tag key={index} color="blue" className="treatment-tag">
                        {item.treatmentName} {item.treatmentsDay && `(${item.treatmentsDay} days)`}
                    </Tag>
                ))}
            </div>
        );
    };

    const renderSolutions = () => {
        if (!consultation.solution || consultation.solution.length === 0) return null;

        return (
            <div className="solution-list">
                {consultation.solution.map((item, index) => (
                    <Tag key={index} color="green" className="solution-tag">
                        {item.solutionName} {item.treatmentsDay && `(${item.treatmentsDay} days)`}
                    </Tag>
                ))}
            </div>
        );
    };

    const renderLaboratory = () => {
        if (!consultation.laboratory || consultation.laboratory.length === 0) return null;

        return (
            <div className="laboratory-section">
                {consultation.laboratory.map((lab, index) => (
                    <div key={lab._id || index} className="lab-category">
                        <h5 className="lab-category-title">
                            <ExperimentOutlined /> {lab.category}
                        </h5>
                        <p className="lab-section-name">{lab.name}</p>
                        <div className="lab-options">
                            {lab.options?.map((option, idx) => (
                                <Tag key={idx} color="purple">{option}</Tag>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderDosAndDonts = () => {
        return (
            <div className="dos-donts-container">
                {consultation.dos && consultation.dos.length > 0 && (
                    <div className="dos-section">
                        <h5 className="section-subtitle" style={{ color: '#52c41a' }}>
                            ✓ Do&apos;s
                        </h5>
                        <ul className="instruction-list">
                            {consultation.dos.map((item, index) => (
                                <li key={item._id || index}>{item.title}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {consultation.donts && consultation.donts.length > 0 && (
                    <div className="donts-section">
                        <h5 className="section-subtitle" style={{ color: '#ff4d4f' }}>
                            ✗ Don&apos;ts
                        </h5>
                        <ul className="instruction-list">
                            {consultation.donts.map((item, index) => (
                                <li key={item._id || index}>{item.title}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    const renderExercises = () => {
        if (!consultation.exercise || consultation.exercise.length === 0) return null;

        return (
            <div className="exercise-list">
                {consultation.exercise.map((item, index) => (
                    <Tag key={item._id || index} color="orange" className="exercise-tag">
                        {item.title}
                    </Tag>
                ))}
            </div>
        );
    };

    return (
        <Card
            className="consultation-history-card"
            style={{ marginBottom: '24px' }}
            title={
                <div className="consultation-header">
                    <div className="header-left">
                        <FileTextOutlined className="header-icon" />
                        <span className="header-title">Previous Consultation Summary</span>
                        <Badge
                            count={consultation.status}
                            style={{
                                backgroundColor: consultation.status === 'Completed' ? '#52c41a' : '#faad14',
                                marginLeft: '12px'
                            }}
                        />
                    </div>
                    <div className="header-right">
                        <CalendarOutlined />
                        <span className="consultation-date">
                            {moment(consultation.dateOfConsultation).format('DD MMM YYYY, hh:mm A')}
                        </span>
                    </div>
                </div>
            }
        >
            {/* Basic Information */}
            <div className="basic-info-section">
                <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small" bordered>
                    <Descriptions.Item label={<><UserOutlined /> Doctor</>}>
                        {consultation.appointmentId?.doctor?.name || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Consultation Type">
                        <Tag color="blue">{consultation.consultationType || 'N/A'}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Treatment">
                        {consultation.appointmentId?.treatment || 'N/A'}
                    </Descriptions.Item>
                    {consultation.rootCause && consultation.rootCause.length > 0 && (
                        <Descriptions.Item label="Root Cause" span={3}>
                            {consultation.rootCause.map((cause, idx) => (
                                <Tag key={idx} color="red">{cause}</Tag>
                            ))}
                        </Descriptions.Item>
                    )}
                </Descriptions>
            </div>

            <Divider />

            {/* Collapsible Sections */}
            <Collapse
                activeKey={expandedSections}
                onChange={setExpandedSections}
                className="consultation-collapse"
                expandIconPosition="end"
            >
                {/* Prescribed Medicines */}
                {consultation.recomendedMedicines && consultation.recomendedMedicines.length > 0 && (
                    <Panel
                        header={
                            <div className="panel-header">
                                <MedicineBoxOutlined className="panel-icon" />
                                <span>Prescribed Medicines</span>
                                <Badge count={consultation.recomendedMedicines.length} showZero />
                            </div>
                        }
                        key="medicines"
                    >
                        {renderMedicines()}
                    </Panel>
                )}

                {/* Diagnosis */}
                {consultation.diagnosis && consultation.diagnosis.length > 0 && (
                    <Panel
                        header={
                            <div className="panel-header">
                                <HeartOutlined className="panel-icon" />
                                <span>Diagnosis</span>
                            </div>
                        }
                        key="diagnosis"
                    >
                        {renderDiagnosis()}
                    </Panel>
                )}

                {/* Treatments & Solutions */}
                {((consultation.treatment && consultation.treatment.length > 0) ||
                    (consultation.solution && consultation.solution.length > 0)) && (
                        <Panel
                            header={
                                <div className="panel-header">
                                    <FileTextOutlined className="panel-icon" />
                                    <span>Treatments & Solutions</span>
                                </div>
                            }
                            key="treatments"
                        >
                            {consultation.treatment && consultation.treatment.length > 0 && (
                                <>
                                    <h5 className="section-subtitle">Treatments</h5>
                                    {renderTreatments()}
                                </>
                            )}
                            {consultation.solution && consultation.solution.length > 0 && (
                                <>
                                    <h5 className="section-subtitle" style={{ marginTop: '16px' }}>Solutions</h5>
                                    {renderSolutions()}
                                </>
                            )}
                        </Panel>
                    )}

                {/* Laboratory Tests */}
                {consultation.laboratory && consultation.laboratory.length > 0 && (
                    <Panel
                        header={
                            <div className="panel-header">
                                <ExperimentOutlined className="panel-icon" />
                                <span>Laboratory Tests</span>
                                <Badge count={consultation.laboratory.length} showZero />
                            </div>
                        }
                        key="laboratory"
                    >
                        {renderLaboratory()}
                    </Panel>
                )}

                {/* Do's and Don'ts */}
                {((consultation.dos && consultation.dos.length > 0) ||
                    (consultation.donts && consultation.donts.length > 0)) && (
                        <Panel
                            header={
                                <div className="panel-header">
                                    <FileTextOutlined className="panel-icon" />
                                    <span>Instructions (Do&apos;s &amp; Don&apos;ts)</span>
                                </div>
                            }
                            key="instructions"
                        >
                            {renderDosAndDonts()}
                        </Panel>
                    )}

                {/* Exercises */}
                {consultation.exercise && consultation.exercise.length > 0 && (
                    <Panel
                        header={
                            <div className="panel-header">
                                <HeartOutlined className="panel-icon" />
                                <span>Recommended Exercises</span>
                                <Badge count={consultation.exercise.length} showZero />
                            </div>
                        }
                        key="exercises"
                    >
                        {renderExercises()}
                    </Panel>
                )}

                {/* Doctor's Notes */}
                {consultation.doctorNotes && consultation.doctorNotes.length > 0 && (
                    <Panel
                        header={
                            <div className="panel-header">
                                <FileTextOutlined className="panel-icon" />
                                <span>Doctor&apos;s Notes</span>
                            </div>
                        }
                        key="notes"
                    >
                        <div className="doctor-notes">
                            {consultation.doctorNotes.map((note, index) => (
                                <p key={index}>{note}</p>
                            ))}
                        </div>
                    </Panel>
                )}

                {/* Additional Details */}
                {(consultation.upshay || consultation.anupshay || consultation.nails || consultation.eyes || consultation.prakruti) && (
                    <Panel
                        header={
                            <div className="panel-header">
                                <FileTextOutlined className="panel-icon" />
                                <span>Additional Clinical Details</span>
                            </div>
                        }
                        key="additional"
                    >
                        <Descriptions column={2} size="small" bordered>
                            {consultation.upshay && <Descriptions.Item label="Upshay">{consultation.upshay}</Descriptions.Item>}
                            {consultation.anupshay && <Descriptions.Item label="Anupshay">{consultation.anupshay}</Descriptions.Item>}
                            {consultation.nails && <Descriptions.Item label="Nails">{consultation.nails}</Descriptions.Item>}
                            {consultation.eyes && <Descriptions.Item label="Eyes">{consultation.eyes}</Descriptions.Item>}
                            {consultation.prakruti && consultation.prakruti.length > 0 && (
                                <Descriptions.Item label="Prakruti" span={2}>
                                    {consultation.prakruti.map((p, idx) => (
                                        <Tag key={idx} color="cyan">{p}</Tag>
                                    ))}
                                </Descriptions.Item>
                            )}
                            {consultation.virechan && consultation.virechan.length > 0 && (
                                <Descriptions.Item label="Virechan" span={2}>
                                    {consultation.virechan.map((v, idx) => (
                                        <Tag key={idx}>{v}</Tag>
                                    ))}
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </Panel>
                )}
            </Collapse>

            {/* Next Consultation Date */}
            {consultation.nextConsultationDate && (
                <div className="next-consultation-banner">
                    <ClockCircleOutlined />
                    <span>
                        Next Consultation Scheduled: <strong>{moment(consultation.nextConsultationDate).format('DD MMM YYYY')}</strong>
                    </span>
                </div>
            )}
        </Card>
    );
};

export default ConsultationHistory;
